import { useQuery } from '@tanstack/react-query';
import { callApi } from '@/hooks/useFetch';
import { hydrateRoleRegistry, RoleRegistryData } from '@/lib/roleRegistry';

// Fetches the role registry (single source of truth) from the backend and
// hydrates the module-level cache that the synchronous role helpers read from.
// Mount once near the app root; React Query dedupes if called elsewhere.
export function useRoleRegistry() {
  const query = useQuery<RoleRegistryData>({
    queryKey: ['roleRegistry'],
    queryFn: () => callApi<RoleRegistryData>('/auth/roles'),
    // Session-static config: fetch once and serve from cache. React Query
    // dedupes every AuthWrapper to this single ['roleRegistry'] entry, and
    // these options stop any auto-refetch (mount/focus/reconnect), so the BE
    // is hit once per session, not on every call.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Hydrate synchronously (idempotent; guarded against the stable data ref) so
  // the cache is ready in the same render that gates on it — no flash.
  if (query.data) {
    hydrateRoleRegistry(query.data);
  }

  return query;
}
